import { Stack, Text, tokens, Link } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import {
  countryRequiresState,
  getCountryNameFromCode,
  getStateNameFromCode,
} from '@hotjar/country-lib';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { Customer } from '~/subscription-management/api/common/model/customer';

interface Props {
  customer: Pick<Customer, 'companyName' | 'billingAddress' | 'taxNumber'>;
  onEdit?: () => void;
  displayEditBelow?: boolean;
  truncateAddress?: boolean;
  asTitle?: boolean;
  className?: string;
}

const S = {
  Container: styled(Stack)`
    align-items: flex-start;
  `,
  TextBlockContainer: styled.div`
    display: flex;
    flex-direction: column;
    max-width: 100%;
  `,
  Header: styled.div`
    align-self: stretch;
    display: flex;
    justify-content: space-between;
  `,
  TruncatedText: styled(Text)`
    ${tokens.truncateText()};
    flex-shrink: 999999;
  `,
  UnstructuredAddress: styled(Text)<{ truncate: boolean }>`
    white-space: pre-line;
    ${(p) => (p.truncate ? tokens.truncateText() : '')};
  `,
};

export function InvoiceDetailsReadonly({
  customer: { companyName, taxNumber = '', billingAddress },
  onEdit,
  displayEditBelow = false,
  truncateAddress = false,
  asTitle = false,
  className,
}: Props): JSX.Element | null {
  const { t } = useTranslationWithPrefix('checkout:invoices.invoice-details-card');

  if (!billingAddress) {
    return null;
  }

  const { addressLine1, addressLine2, zipCode, city, country, state, county, unstructuredAddress } =
    billingAddress;

  const FullAddress = () => (
    <>
      <Text>{addressLine1}</Text>
      {addressLine2 !== '' && <Text>{addressLine2}</Text>}
      <Text>
        {zipCode}, {city}, {getCountryNameFromCode(country)}
      </Text>
      {countryRequiresState(country) &&
        state !== null &&
        state !== undefined &&
        state.length > 0 && (
          <Text>
            {getStateNameFromCode(state, country)}
            {county !== '' ? `, ${county}` : ''}
          </Text>
        )}
    </>
  );

  const TruncatedAddress = () => (
    <Stack customSpacing="2px">
      <S.TruncatedText>
        {addressLine1}, {addressLine2 !== '' ? `${addressLine2},` : ''} {city},
      </S.TruncatedText>
      <Text>{getCountryNameFromCode(country)}</Text>
    </Stack>
  );

  const EditLink = () =>
    onEdit !== undefined ? (
      <Link as="button" kind="standalone" onClick={onEdit}>
        {t('edit')}
      </Link>
    ) : null;

  // For migrated B2.0 customers, who haven't updated their Invoice Details, we're
  //  displaying and unstructured address
  const displayUnstructuredAddress = addressLine1 === null || addressLine1 === undefined;

  return (
    <S.Container spacing="x1" direction="column" className={className}>
      <S.Header>
        {asTitle ? (
          <Text textType="header5" as="h2">
            {t('title')}
          </Text>
        ) : (
          <Text textType="subtitle2">{t('title')}</Text>
        )}

        {!displayEditBelow && <EditLink />}
      </S.Header>
      <S.TextBlockContainer>
        <Text>{companyName}</Text>
        {displayUnstructuredAddress && (
          <S.UnstructuredAddress truncate={truncateAddress}>
            {/* If unstructuredAddress is an empty string, we will always have the country available, so we show that instead  */}
            {unstructuredAddress === '' ? getCountryNameFromCode(country) : unstructuredAddress}
          </S.UnstructuredAddress>
        )}

        {!displayUnstructuredAddress && (truncateAddress ? <TruncatedAddress /> : <FullAddress />)}
      </S.TextBlockContainer>

      {taxNumber !== '' && (
        <S.TextBlockContainer>
          {truncateAddress ? (
            <Text>
              {t('tax-number')} {taxNumber}
            </Text>
          ) : (
            <>
              <Text>{t('tax-number-extra-info')}</Text>
              <Text>{taxNumber}</Text>
            </>
          )}
        </S.TextBlockContainer>
      )}

      {displayEditBelow && <EditLink />}
    </S.Container>
  );
}
